import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Newspaper, ExternalLink, ArrowLeft, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Seo } from "../components/Seo";
import { GuidesGrid } from "../components/guide/GuidesGrid";

const isNewStudy = (iso) => iso && Date.now() - new Date(iso).getTime() < 7 * 24 * 3600 * 1000;

export default function Research() {
  const [posts, setPosts] = useState(null);
  const [guides, setGuides] = useState([]);
  const { hash } = useLocation();

  useEffect(() => {
    api.get("/bulletin?limit=100")
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]));
    api.get("/guides").then(({ data }) => setGuides(data)).catch(() => setGuides([]));
  }, []);

  useEffect(() => {
    if (hash === "#guides" && guides.length) document.getElementById("guides")?.scrollIntoView({ behavior: "smooth" });
  }, [hash, guides.length]);

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="research-page">
      <Seo
        title="Peptide Research Guides & Bulletin"
        description="Citation-backed research guides on every peptide we carry plus plain-language summaries of published academic studies — BPC-157, TB-500, GLP-1 analogues and more. No dosing guidance."
        path="/research"
        jsonLd={
          posts?.length
            ? {
                "@context": "https://schema.org",
                "@type": "ItemList",
                name: "Peptide Research Bulletin",
                itemListElement: posts.slice(0, 25).map((p, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  item: {
                    "@type": "ScholarlyArticle",
                    headline: p.title,
                    description: p.summary,
                    ...(p.source_url && { url: p.source_url }),
                    ...(p.published_at && { datePublished: p.published_at }),
                  },
                })),
              }
            : undefined
        }
      />
      <Link to="/" data-testid="research-back-link" className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-semibold text-[#6E675F] hover:text-[#1A1A1A] transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>
      <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-2">Eternal Biolabs Research Desk</p>
      <h1 className="font-display text-xl sm:text-3xl uppercase tracking-wide text-[#1A1A1A] mb-3">Research</h1>
      <p className="text-base text-[#6E675F] max-w-2xl mb-14">
        Reference guides on each peptide we carry and plain-language summaries of published studies — curated from the scientific literature. For laboratory research only; no dosing or usage guidance.
      </p>

      <GuidesGrid guides={guides} />

      <div className="flex items-center gap-3 mb-3">
        <Newspaper className="w-6 h-6 text-[#8C7B6B]" />
        <h2 className="font-display text-base sm:text-lg uppercase tracking-wide text-[#1A1A1A]">Research Bulletin</h2>
        <div className="flex-1 h-px bg-[#DDD7CF]" />
      </div>
      <p className="text-sm text-[#6E675F] max-w-2xl mb-10">
        Plain-language summaries of published academic studies on research peptides, curated from the scientific literature.
      </p>

      {posts === null ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <p className="text-sm text-[#6E675F] py-16 text-center" data-testid="research-empty">No studies published yet — check back soon.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="research-grid">
          {posts.map((p) => (
            <article key={p.id} className="border border-[#DDD7CF] rounded-lg bg-white p-5 sm:p-6 flex flex-col hover:border-[#8C7B6B] transition-colors" data-testid={`research-card-${p.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                {isNewStudy(p.published_at) && (
                  <span className="inline-flex text-[10px] tracking-[0.1em] uppercase font-bold bg-[#EAF2EC] text-[#2D6A4F] border border-[#2D6A4F]/25 rounded-none px-2.5 py-1" data-testid={`research-new-tag-${p.id}`}>
                    New this week
                  </span>
                )}
                {p.peptide && (
                  <span className="inline-flex text-[10px] tracking-[0.1em] uppercase font-bold bg-[#EFEBE6] text-[#8C7B6B] rounded-none px-2.5 py-1">
                    {p.peptide}
                  </span>
                )}
                <span className="text-[11px] text-[#6E675F] ml-auto">{p.journal}{p.year ? ` · ${p.year}` : ""}</span>
              </div>
              <h2 className="text-sm font-semibold text-[#1A1A1A] mt-3 leading-snug">{p.title}</h2>
              <p className="text-sm text-[#6E675F] mt-2 leading-relaxed flex-1">{p.summary}</p>
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                {p.source_url && (
                  <a
                    href={p.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`research-card-link-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A] transition-colors"
                  >
                    Read the study <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {p.shop_product_id && (
                  <Link
                    to={`/product/${p.shop_product_id}`}
                    data-testid={`research-shop-link-${p.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#8C7B6B] text-white text-[11px] tracking-[0.12em] uppercase font-semibold px-4 py-2 rounded-none hover:bg-[#75665A] transition-colors"
                  >
                    Shop {p.shop_product_name} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <p className="text-[11px] text-[#6E675F] mt-8">
        Summaries reference published academic research. For informational purposes only — not medical or dosing guidance.
      </p>
    </div>
  );
}
